import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    maxWidth?: string;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, maxWidth = '600px' }) => {
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
            <div
                ref={modalRef}
                className="rounded-lg shadow-xl w-full max-w-lg transform transition-all fade-in card p-0"
                role="dialog"
                aria-modal="true"
                style={{ backgroundColor: '#111111', borderRadius: '1rem', boxShadow: '0 25px 50px rgba(0,0,0,0.6)', width: '100%', maxWidth, maxHeight: '90vh', display: 'flex', flexDirection: 'column', border: '1px solid #2a2a2a' }}
            >
                <div className="flex justify-between items-center p-lg" style={{ padding: 'var(--spacing-lg)', borderBottom: '1px solid #2a2a2a' }}>
                    <h3 className="text-lg font-bold m-0" style={{ color: '#e5e5e5' }}>{title}</h3>
                    <button
                        onClick={onClose}
                        className="focus:outline-none"
                        style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#737373', transition: 'color 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#e5e5e5'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#737373'}
                    >
                        &times;
                    </button>
                </div>

                <div className="p-lg overflow-y-auto" style={{ padding: 'var(--spacing-lg)', overflowY: 'auto' }}>
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default Modal;
